import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { telegramVibrate } from '@app/shared/utils/common';
import { Settlement } from '@app/store/slices/settlements/settlementsTypes';
import { Page } from '@app/routes';
import { SettlementModalAction } from '@app/shared/types/pages/home';
import notification from '@app/shared/utils/notification';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { tutorialActions } from '@app/store/slices/tutorial/tutorialSlice';

interface UseSettlementClickActionParams {
  settlement: Settlement;
  onSettlementClick?: (settlement: Settlement, action: SettlementModalAction) => void;
}

export const useSettlementClickAction = ({
  settlement,
  onSettlementClick,
}: UseSettlementClickActionParams) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const tutorial = useAppSelector((state) => state.tutorial);
  const navigate = useNavigate();

  const handleNavigateToSettlementsDetails = useCallback(() => {
    if (settlement.hasDetails) {
      void navigate(Page.SETTLEMENT_DETAILS.replace(':id', String(settlement.id)));
    } else {
      notification.warning(t('settlements.not_ready'));
    }
  }, [navigate, settlement.id, settlement.hasDetails]);

  const getOnClickAction = useCallback(() => {
    if (settlement.isLocked) {
      return () => {
        notification.info(t('common.soon'));
      };
    } else if (settlement.isBoosted) {
      return () => {
        if (
          !tutorial.complete &&
          tutorial.step === 'HOME_SETTLEMENT_1' &&
          settlement.nodeLevel === 0 &&
          pathname === '/'
        ) {
          dispatch(tutorialActions.onSettlementClick());
        }
        handleNavigateToSettlementsDetails();
      };
    } else if (!settlement.isPurchased) {
      return () => {
        onSettlementClick?.(settlement, 'buy');
      };
    }
    return () => {
      onSettlementClick?.(settlement, 'boost');
    };
  }, [settlement, handleNavigateToSettlementsDetails, onSettlementClick]);

  const onClickHandler = useCallback(() => {
    const action = getOnClickAction();
    action();
    telegramVibrate();
  }, [getOnClickAction]);

  return { onClickHandler };
};
